import { Box, Stack, Text } from '@atlaskit/primitives';
import { useDialog } from '@databases/dialog';
import { messages } from '@databases/intl/messages';
import { type PanelManager } from '@databases/panel-types';
import { doDeleteFieldsWithIds } from '@databases/redux/contextual-menu/actions';
import { appStore } from '@databases/redux/store';
import { type SelectionsApi } from '@databases/selections-api';
import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';

type UseDeleteFieldsActionProps = {
  selectionsApi: SelectionsApi;
  panelManager: PanelManager;
  contentId: string;
};

/**
 * Provides the delete fields action using the selections API instead of redux.
 * Shows a confirmation dialog before deleting the selected fields.
 */
export const useDeleteFieldsAction = ({
  selectionsApi,
  panelManager,
  contentId,
}: UseDeleteFieldsActionProps) => {
  const { formatMessage } = useIntl();
  const { showDialog } = useDialog();

  const handleDeleteFields = useCallback(() => {
    const resolved = selectionsApi.getResolvedSelection();

    if (resolved.mode !== 'column') {
      return;
    }

    const fieldIds = resolved.fields.map((f) => f.fieldId);
    const fieldNames = resolved.fields.map((f) => f.field.name);

    if (fieldIds.length === 0) {
      return;
    }

    const panelData = panelManager.getData('FieldSettingsPanel');
    const panelFieldId = panelData?.fieldId;

    showDialog('ConfirmDialog', {
      heading: formatMessage(messages['field-settings.delete-field']),
      body: (
        <Stack space="space.150">
          <Box>
            <FormattedMessage
              {...messages[
                'field-settings.delete-multiple-fields-confirmation-message.fields'
              ]}
              values={{
                fieldsNumber: fieldNames.length,
                fields: <Text weight="bold">{fieldNames.join(', ')}</Text>,
              }}
            />
          </Box>
          <Box>
            {formatMessage(
              messages[
                'field-settings.delete-multiple-fields-confirmation-message.additional'
              ]
            )}
          </Box>
        </Stack>
      ),
      yesLabel: formatMessage(messages['delete-buttons.delete-field-button']),
      noLabel: formatMessage(messages['delete-buttons.delete-field-cancel-button']),
      appearance: 'danger',
      onClose: (result: boolean) => {
        selectionsApi.clearPreviewIntent();

        if (result) {
          if (panelFieldId && fieldIds.includes(panelFieldId)) {
            panelManager.hidePanel();
          }

          appStore.dispatch(doDeleteFieldsWithIds(contentId, fieldIds));
          selectionsApi.deselectAll();
        }
      },
    });
  }, [contentId, formatMessage, panelManager, selectionsApi, showDialog]);

  return handleDeleteFields;
};
